'use client';
import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import type { BookingState } from './BookingWizard';
import { bookingService, visitorService } from '@/lib/services/golfService';
import { emailService } from '@/lib/services/emailService';
import { notificationService } from '@/lib/services/notificationService';
import { trackPaymentInitiated, trackBookingConversion } from '@/lib/analytics';

interface Props {
  booking: BookingState;
  updateBooking: (u: Partial<BookingState>) => void;
  onBack: () => void;
  onConfirm: () => void;
}

const PAYMENT_METHODS = [
  { id: 'credit', label: 'Credit Card', sub: 'Visa / Mastercard', icon: 'CreditCardIcon' },
  { id: 'debit', label: 'Debit Card', sub: 'Visa / Maestro', icon: 'CreditCardIcon' },
  { id: 'eft', label: 'EFT Payment', sub: 'Instant bank transfer', icon: 'BanknotesIcon' },
  { id: 'snapscan', label: 'SnapScan', sub: 'Scan & pay instantly', icon: 'QrCodeIcon' },
  { id: 'zapper', label: 'Zapper', sub: 'Scan & pay instantly', icon: 'QrCodeIcon' },
];

export default function PaymentStep({ booking, updateBooking, onBack, onConfirm }: Props) {
  const [selectedMethod, setSelectedMethod] = useState('credit');
  const [isProcessing, setIsProcessing] = useState(false);
  const [payfastFormData, setPayfastFormData] = useState<Record<string, string> | null>(null);
  const [payfastUrl, setPayfastUrl] = useState('');

  const displayDate = booking.selectedDate
    ? new Date(booking.selectedDate + 'T12:00:00').toLocaleDateString('en-ZA', {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : '';

  // Auto-submit PayFast form when data is ready
  useEffect(() => {
    if (payfastFormData && payfastUrl) {
      const form = document.getElementById('payfast-redirect-form') as HTMLFormElement;
      if (form) {
        form.submit();
      }
    }
  }, [payfastFormData, payfastUrl]);

  const handlePayment = async () => {
    setIsProcessing(true);
    try {
      // Step 1: Upsert visitor
      let visitorId: string | undefined;
      const vd = booking.visitorData;
      if (vd?.email) {
        const visitor = await visitorService.upsertByEmail({
          firstName: String(vd.firstName || ''),
          surname: String(vd.surname || ''),
          email: String(vd.email),
          mobile: String(vd.mobile || ''),
          dateOfBirth: vd.dateOfBirth ? String(vd.dateOfBirth) : undefined,
          idNumber: vd.idNumber ? String(vd.idNumber) : undefined,
          address: vd.address ? String(vd.address) : undefined,
          emergencyContact: vd.emergencyContact ? String(vd.emergencyContact) : undefined,
          emergencyPhone: vd.emergencyPhone ? String(vd.emergencyPhone) : undefined,
          homeClub: vd.homeClub ? String(vd.homeClub) : undefined,
          handicap: vd.handicap ? Number(vd.handicap) : undefined,
          cdhNumber: vd.cdhNumber ? String(vd.cdhNumber) : undefined,
          marketingOptIn: !!(booking.consentData?.marketingOptIn),
        });
        visitorId = visitor?.id;
      }

      // Step 2: Create booking record (pending status)
      const visitorName = vd?.firstName ? `${vd.firstName} ${vd.surname}` : 'Guest';
      const visitorEmail = vd?.email ? String(vd.email) : '';

      const newBooking = await bookingService.create({
        visitorId,
        visitorName: String(visitorName),
        visitorEmail,
        date: booking.selectedDate,
        time: booking.selectedTime,
        players: booking.players,
        holes: booking.holes,
        cart: booking.cart,
        playerNames: booking.playerNames,
        greenFees: booking.greenFees,
        cartFees: booking.cartFees,
        total: booking.total,
        paymentMethod: selectedMethod,
        consentSigned: !!(booking.consentData?.indemnity),
        marketingOptIn: !!(booking.consentData?.marketingOptIn),
      });

      if (!newBooking?.id) {
        throw new Error('Failed to create booking record');
      }

      // Track booking conversion
      trackBookingConversion(booking.total, newBooking.id, booking.players, booking.holes);

      // Step 3: Increment visitor rounds
      if (visitorId) {
        await visitorService.incrementRounds(visitorId);
      }

      // Step 4: Send booking confirmation email
      if (visitorEmail) {
        await emailService.sendBookingConfirmation({
          to: visitorEmail,
          visitorName: String(visitorName),
          date: displayDate,
          time: booking.selectedTime,
          players: booking.players,
          holes: booking.holes,
          cart: booking.cart,
          paymentMethod: selectedMethod,
          total: booking.total,
        });

        // In-app notification alongside email
        await notificationService.create({
          visitorEmail,
          type: 'booking_confirmation',
          title: 'Booking Confirmed! ⛳',
          message: `Your tee time on ${displayDate} at ${booking.selectedTime} for ${booking.players} player${booking.players > 1 ? 's' : ''} (${booking.holes} holes) is confirmed. Total: R${booking.total}.`,
          metadata: {
            date: booking.selectedDate,
            time: booking.selectedTime,
            players: booking.players,
            holes: booking.holes,
            total: booking.total,
          },
        });
      }

      // Step 5: Initiate PayFast payment
      const itemName = `Maritzburg Golf Club – ${booking.holes} Holes, ${booking.players} Player${booking.players > 1 ? 's' : ''}`;

      // Track payment initiation
      trackPaymentInitiated(booking.total, booking.players, booking.holes);

      const response = await fetch('/api/payfast/initiate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: newBooking.id,
          visitorName: String(visitorName),
          visitorEmail,
          amount: booking.total,
          itemName,
          paymentMethod: selectedMethod,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to initiate PayFast payment');
      }

      const { payfastUrl: pfUrl, formData } = await response.json();

      updateBooking({ paymentMethod: selectedMethod });

      // Trigger redirect to PayFast
      setPayfastUrl(pfUrl);
      setPayfastFormData(formData);

    } catch (error: any) {
      toast.error(error?.message || 'Payment initiation failed. Please try again.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
      {/* Hidden PayFast redirect form */}
      {payfastFormData && payfastUrl && (
        <form
          id="payfast-redirect-form"
          action={payfastUrl}
          method="POST"
          style={{ display: 'none' }}
        >
          {Object.entries(payfastFormData).map(([key, value]) => (
            <input key={key} type="hidden" name={key} value={value} />
          ))}
        </form>
      )}

      <div className="xl:col-span-2 space-y-6">
        {/* Payment Method Selection */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card">
          <div className="flex items-center gap-3 mb-5">
            <h2 className="text-lg font-bold text-foreground">Select Payment Method</h2>
            <div className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-full">
              <Icon name="LockClosedIcon" size={11} variant="outline" />
              Secured by PayFast
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PAYMENT_METHODS.map((method) => (
              <button
                key={`pay-${method.id}`}
                type="button"
                onClick={() => setSelectedMethod(method.id)}
                className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all duration-150 ${
                  selectedMethod === method.id
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/30 bg-card'
                }`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  selectedMethod === method.id ? 'bg-primary' : 'bg-muted'
                }`}>
                  <Icon
                    name={method.icon as Parameters<typeof Icon>[0]['name']}
                    size={18}
                    variant="outline"
                    className={selectedMethod === method.id ? 'text-white' : 'text-muted-foreground'}
                  />
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground">{method.label}</div>
                  <div className="text-xs text-muted-foreground">{method.sub}</div>
                </div>
                {selectedMethod === method.id && (
                  <div className="ml-auto w-5 h-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                    <Icon name="CheckIcon" size={12} variant="outline" className="text-white" />
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Payment Info Panel */}
        <div className="bg-card border border-border rounded-2xl p-6 shadow-card fade-in">
          {selectedMethod === 'credit' || selectedMethod === 'debit' ? (
            <div className="text-center py-2">
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-4">
                <Icon name="CreditCardIcon" size={28} variant="outline" className="text-white" />
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">
                {selectedMethod === 'credit' ? 'Credit Card' : 'Debit Card'} Payment
              </h3>
              <p className="text-muted-foreground text-sm mb-4">
                You will be securely redirected to PayFast to complete your{' '}
                {selectedMethod === 'credit' ? 'credit' : 'debit'} card payment of{' '}
                <span className="font-bold text-foreground">R{booking.total}</span>.
              </p>
              <div className="flex flex-wrap justify-center gap-2 mb-2">
                {['Visa', 'Mastercard', '3D Secure'].map((badge) => (
                  <span key={badge} className="text-xs bg-muted px-3 py-1 rounded-full font-medium text-muted-foreground">
                    {badge}
                  </span>
                ))}
              </div>
            </div>
          ) : selectedMethod === 'eft' ? (
            <div className="text-center py-2">
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-4">
                <Icon name="BanknotesIcon" size={28} variant="outline" className="text-white" />
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">EFT Payment</h3>
              <p className="text-muted-foreground text-sm mb-4">
                You will be redirected to PayFast to complete your EFT payment of{' '}
                <span className="font-bold text-foreground">R{booking.total}</span> via instant bank transfer.
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {['Absa', 'FNB', 'Nedbank', 'Standard Bank', 'Capitec'].map((bank) => (
                  <span key={bank} className="text-xs bg-muted px-3 py-1 rounded-full font-medium text-muted-foreground">
                    {bank}
                  </span>
                ))}
              </div>
            </div>
          ) : selectedMethod === 'snapscan' ? (
            <div className="text-center py-2">
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-4">
                <Icon name="QrCodeIcon" size={28} variant="outline" className="text-white" />
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">SnapScan Payment</h3>
              <p className="text-muted-foreground text-sm">
                You will be redirected to PayFast to complete your SnapScan payment of{' '}
                <span className="font-bold text-foreground">R{booking.total}</span>.
                Have your SnapScan app ready to scan the QR code.
              </p>
            </div>
          ) : selectedMethod === 'zapper' ? (
            <div className="text-center py-2">
              <div className="w-16 h-16 rounded-2xl gradient-primary flex items-center justify-center mx-auto mb-4">
                <Icon name="QrCodeIcon" size={28} variant="outline" className="text-white" />
              </div>
              <h3 className="font-bold text-foreground text-lg mb-2">Zapper Payment</h3>
              <p className="text-muted-foreground text-sm">
                You will be redirected to PayFast to complete your Zapper payment of{' '}
                <span className="font-bold text-foreground">R{booking.total}</span>.
                Have your Zapper app ready to scan the QR code.
              </p>
            </div>
          ) : null}

          {/* PayFast redirect notice */}
          <div className="mt-5 bg-muted/40 border border-border rounded-xl p-4 flex items-start gap-3">
            <Icon name="InformationCircleIcon" size={16} variant="outline" className="text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Clicking <strong className="text-foreground">Pay Now</strong> will create your booking and redirect you to the secure PayFast payment page. Your booking is confirmed once payment is complete.
            </p>
          </div>

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onBack}
              disabled={isProcessing}
              className="flex-1 py-3.5 rounded-xl border-2 border-border text-foreground font-semibold text-sm hover:bg-muted transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Icon name="ChevronLeftIcon" size={16} variant="outline" />
              Back
            </button>
            <button
              type="button"
              onClick={handlePayment}
              disabled={isProcessing}
              className="flex-1 btn-gold py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-70"
            >
              {isProcessing ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Redirecting to PayFast...
                </>
              ) : (
                <>
                  <Icon name="LockClosedIcon" size={16} variant="outline" />
                  Pay Now — R{booking.total}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Booking Summary Sidebar */}
      <div className="space-y-4">
        <div className="bg-card border border-border rounded-2xl p-5 shadow-card sticky top-20">
          <h3 className="font-bold text-foreground text-base mb-4 flex items-center gap-2">
            <Icon name="ReceiptPercentIcon" size={18} variant="outline" className="text-primary" />
            Final Booking Summary
          </h3>
          <div className="space-y-2 mb-4 pb-4 border-b border-border text-sm">
            <div className="flex justify-between text-muted-foreground">
              <span>Date</span>
              <span className="text-foreground font-medium">{displayDate}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Tee Time</span>
              <span className="text-foreground font-semibold font-tabular">{booking.selectedTime}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Holes</span>
              <span className="text-foreground font-medium">{booking.holes}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Players</span>
              <span className="text-foreground font-medium">{booking.players}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Cart</span>
              <span className="text-foreground font-medium capitalize">
                {booking.cart === 'none' ? 'Walking' : booking.cart}
              </span>
            </div>
          </div>
          <div className="space-y-2 mb-4 pb-4 border-b border-border text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Green Fees</span>
              <span className="font-semibold font-tabular">R{booking.greenFees}</span>
            </div>
            {booking.cartFees > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cart Hire</span>
                <span className="font-semibold font-tabular">R{booking.cartFees}</span>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center mb-4">
            <span className="font-bold text-foreground text-base">Total Due</span>
            <span className="text-3xl font-extrabold text-primary font-tabular">R{booking.total}</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
            <Icon name="LockClosedIcon" size={13} variant="outline" className="shrink-0" />
            <span>Secured by PayFast · 256-bit SSL encryption · PCI DSS Level 1</span>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-4 shadow-card">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Cancellation Policy</h4>
          <ul className="space-y-1.5 text-xs text-muted-foreground">
            <li className="flex items-start gap-2">
              <Icon name="CheckCircleIcon" size={12} variant="outline" className="text-secondary shrink-0 mt-0.5" />
              Full refund if cancelled 24+ hours before tee time
            </li>
            <li className="flex items-start gap-2">
              <Icon name="XCircleIcon" size={12} variant="outline" className="text-red-400 shrink-0 mt-0.5" />
              Non-refundable within 24 hours of tee time
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}